import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { ProgressSpinner } from 'primereact/progressspinner';
import { DataTable as PrimeDataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Sidebar } from 'primereact/sidebar';
import { Tooltip } from 'primereact/tooltip';
import { Toast } from 'primereact/toast';
import { Dialog } from 'primereact/dialog';

import { AppCard, AppPage, CopyableText } from '../../components/general';
import { useVoxOrderHistoryDetail, useVendorCapabilities } from '../../hooks/useVoxTracing';
import { createEmbedUrl, getFileContent } from '../../services/voxTracingService';
import {
  VoxOrderItemHistoryViewModel,
  VoxOrderHistorySummaryViewModel,
  WebhookEventViewModel,
} from '../../models/voxTracing';
import { ProcessedTraceLog, GroupedTraceLogs } from '../../models/tracing';
import { processTraceLogs, groupTraceLogs } from '../../utils/traceLogUtils';
import { toLocalDateTime, toLocalDateTimePrecise } from '../../utils/date-helper';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import TraceLogDetailPanel from '../../components/tracing/TraceLogDetailPanel';
import styles from '../../components/tracing/TraceLogList.module.css';

const VoxOrderDetail = () => {
  const { voxOrderGuid } = useParams<{ voxOrderGuid: string }>();
  const navigate = useNavigate();
  const toast = useRef<Toast>(null);
  const queryClient = useQueryClient();

  const { detailQuery }= useVoxOrderHistoryDetail(voxOrderGuid);
  const { capabilitiesQuery } = useVendorCapabilities(detailQuery.data?.integrationVendor);

  const [selectedLog, setSelectedLog] = useState<ProcessedTraceLog | null>(null);
  const [sidePanelVisible, setSidePanelVisible] = useState(false);

  const [selectedWebhook, setSelectedWebhook] = useState<WebhookEventViewModel | null>(null);
  const [webhookPanelVisible, setWebhookPanelVisible] = useState(false);

  const webhookPayloadQuery = useQuery<string>({
    queryKey: ['file-content', selectedWebhook?.requestPayloadFileId],
    queryFn: () => getFileContent(selectedWebhook!.requestPayloadFileId!),
    enabled: !!selectedWebhook?.requestPayloadFileId && webhookPanelVisible,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  const [dataPanelVisible, setDataPanelVisible] = useState(false);
  const [dataPanelTitle, setDataPanelTitle] = useState('');
  const [dataPanelContent, setDataPanelContent] = useState('');

  const [urlModalVisible, setUrlModalVisible] = useState(false);
  const [generatedUrl, setGeneratedUrl] = useState('');
  const [generatingUrl, setGeneratingUrl] = useState(false);
  const [urlCopied, setUrlCopied] = useState(false);
  const [urlError, setUrlError] = useState<string | null>(null);
  const [iframeModalVisible, setIframeModalVisible] = useState(false);

  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [hasInitialized, setHasInitialized] = useState(false);

  const processedLogs = detailQuery.data?.traceLogs ? processTraceLogs(detailQuery.data.traceLogs) : [];
  const groupedLogs = processedLogs.length > 0 ? groupTraceLogs(processedLogs) : [];

  useEffect(() => {
    if (!hasInitialized && groupedLogs && groupedLogs.length > 0) {
      const allGroupIds = new Set<string>();
      groupedLogs.forEach((group) => {
        if (group.traceGroup && !group.traceGroup.includes(':')) {
          allGroupIds.add(`tier2-${group.traceGroup}`);
          if (group.children && group.children.length > 0) {
            allGroupIds.add(`tier3-${group.traceGroup}`);
          }
        }
      });
      setExpandedGroups(allGroupIds);
      setHasInitialized(true);
    }
  }, [groupedLogs, hasInitialized]);

  const formatDateTime = (value: string | null) => {
    return value ? toLocalDateTime(value) : '—';
  };

  const formatTraceDateTime = (timestamp: string) => {
    return toLocalDateTimePrecise(timestamp) || timestamp;
  };

  const handleViewDetails = (log: ProcessedTraceLog) => {
    setSelectedLog(log);
    setSidePanelVisible(true);
  };

  const toggleGroup = (groupId: string) => {
    setExpandedGroups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(groupId)) {
        newSet.delete(groupId);
      } else {
        newSet.add(groupId);
      }
      return newSet;
    });
  };

  const getAllGroupIds = () => {
    const allGroupIds = new Set<string>();
    if (groupedLogs) {
      groupedLogs.forEach((group) => {
        if (group.traceGroup && !group.traceGroup.includes(':')) {
          allGroupIds.add(`tier2-${group.traceGroup}`);
          if (group.children && group.children.length > 0) {
            allGroupIds.add(`tier3-${group.traceGroup}`);
          }
        }
      });
    }
    return allGroupIds;
  };

  const handleExpandAll = () => setExpandedGroups(getAllGroupIds());
  const handleCollapseAll = () => setExpandedGroups(new Set());

  const statusSeverity: Record<string, 'success' | 'info' | 'warning' | 'danger'> = {
    Success: 'success',
    Pending: 'info',
    AwaitingCallback: 'info',
    Failed: 'danger',
    Error: 'danger',
    Timeout: 'warning',
  };

  const providerDataStatusSeverity: Record<string, 'success' | 'info' | 'warning' | 'danger'> = {
    Unknown: 'info',
    NoData: 'warning',
    EmploymentOnly: 'info',
    IncomeAndEmployment: 'success',
    Assets: 'info',
    AssetsAndIncome: 'success',
  };

  const itemStatusTemplate = (rowData: VoxOrderItemHistoryViewModel) => {
    const severityMap: Record<string, 'success' | 'info' | 'warning' | 'danger'> = {
      Connected: 'success',
      Disconnected: 'danger',
      Unknown: 'info',
    };
    return <Tag value={rowData.status} severity={severityMap[rowData.status] || 'info'} />;
  };

  const renderLog = (log: ProcessedTraceLog, indentLevel: number = 0, isLastInGroup: boolean = false, isTier3InLastTier2: boolean = false) => {
    const hasDetails = !!(log.contextProperties || log.eventDataFileId);
    const descriptionIndent = indentLevel * 3;
    const bgColors = ['transparent', '#fafafa', '#f5f5f5', '#f0f0f0'];
    const bgColor = bgColors[indentLevel] || bgColors[bgColors.length - 1];

    return (
      <div
        key={`${log.id}`}
        className={`flex justify-content-between align-items-center py-2 hover:bg-blue-50 ${hasDetails ? 'cursor-pointer' : ''}`}
        style={{ backgroundColor: bgColor }}
        onClick={(e) => {
          const target = e.target as HTMLElement;
          if (target.closest('.trace-group-chip')) return;
          if (hasDetails) handleViewDetails(log);
        }}
      >
        <div className="flex gap-3 align-items-center flex-1">
          <div className={styles.chevronSpacer} />
          <span className={`text-sm text-gray-500 font-mono ${styles.timestamp}`}>
            {formatTraceDateTime(log.eventTimestamp)}
          </span>
          <span className={`text-base flex-1 ${styles.description}`} style={{ marginLeft: indentLevel > 0 ? `calc(${descriptionIndent}rem + 8px)` : `${descriptionIndent}rem` }}>
            {indentLevel === 1 && (
              <>
                <div className={styles.verticalLine} style={{ left: `${-descriptionIndent / 2}rem`, top: '-0.5rem', bottom: isLastInGroup ? '50%' : '-0.5rem' }} />
                <div className={styles.horizontalLine} style={{ left: `${-descriptionIndent / 2}rem`, top: '50%', width: `${descriptionIndent / 2}rem` }} />
              </>
            )}
            {indentLevel === 2 && (
              <>
                <div className={styles.verticalLine} style={{ left: `${-descriptionIndent + (3 / 2)}rem`, top: '-0.5rem', bottom: (isLastInGroup && isTier3InLastTier2) ? '50%' : '-0.5rem' }} />
                <div className={styles.verticalLine} style={{ left: `${-descriptionIndent / 4}rem`, top: '-0.5rem', bottom: isLastInGroup ? '50%' : '-0.5rem' }} />
                <div className={styles.horizontalLine} style={{ left: `${-descriptionIndent / 4}rem`, top: '50%', width: `${descriptionIndent / 4}rem` }} />
              </>
            )}
            {log.description || 'Event'}
          </span>
          {log.traceGroup && (
            <Tag
              value={log.traceGroup}
              className={`text-xs font-mono trace-group-chip ${styles.traceGroupChip}`}
              data-pr-tooltip={log.traceGroupDescription || log.description || log.traceGroup}
              data-pr-position="top"
            />
          )}
        </div>
      </div>
    );
  };

  const renderFlatLogs = (groups: GroupedTraceLogs[]) => {
    const tier1Logs: ProcessedTraceLog[] = [];
    const tier2ByGroup: Map<string, ProcessedTraceLog[]> = new Map();
    const tier3ByParent: Map<string, ProcessedTraceLog[]> = new Map();

    groups.forEach((group) => {
      group.logs.forEach((log) => {
        if (!log.traceGroup) {
          tier1Logs.push(log);
        } else if (!log.traceGroup.includes(':')) {
          if (!tier2ByGroup.has(log.traceGroup)) tier2ByGroup.set(log.traceGroup, []);
          tier2ByGroup.get(log.traceGroup)!.push(log);
        }
      });
      if (group.traceGroup && !group.traceGroup.includes(':') && group.children && group.children.length > 0) {
        const parentGroup = group.traceGroup;
        if (!tier3ByParent.has(parentGroup)) tier3ByParent.set(parentGroup, []);
        group.children.forEach((childGroup) => {
          tier3ByParent.get(parentGroup)!.push(...childGroup.logs);
        });
      }
    });

    tier1Logs.sort((a, b) => new Date(a.eventTimestamp).getTime() - new Date(b.eventTimestamp).getTime());
    tier2ByGroup.forEach((logs) => logs.sort((a, b) => new Date(a.eventTimestamp).getTime() - new Date(b.eventTimestamp).getTime()));
    tier3ByParent.forEach((logs) => logs.sort((a, b) => new Date(a.eventTimestamp).getTime() - new Date(b.eventTimestamp).getTime()));

    const timeline: Array<{ type: 'tier1' | 'tier2Group'; log: ProcessedTraceLog; groupLogs?: ProcessedTraceLog[] }> = [];
    tier1Logs.forEach((log) => timeline.push({ type: 'tier1', log }));
    tier2ByGroup.forEach((logs) => timeline.push({ type: 'tier2Group', log: logs[0], groupLogs: logs }));
    timeline.sort((a, b) => new Date(a.log.eventTimestamp).getTime() - new Date(b.log.eventTimestamp).getTime());

    const allElements: JSX.Element[] = [];

    timeline.forEach((item) => {
      if (item.type === 'tier1') {
        allElements.push(renderLog(item.log));
      } else if (item.type === 'tier2Group' && item.groupLogs) {
        const groupId = item.log.traceGroup!;
        const tier2Logs = item.groupLogs;
        const firstTier2 = tier2Logs[0];
        const tier2GroupId = `tier2-${groupId}`;
        const isTier2Expanded = expandedGroups.has(tier2GroupId);

        allElements.push(
          <div key={`toggle-${tier2GroupId}`} className={`flex align-items-center py-2 hover:bg-blue-50 ${styles.toggleRow}`}>
            <div className="flex gap-3 align-items-center flex-1">
              <div
                className={`chevron-area flex align-items-center justify-content-center cursor-pointer ${styles.chevronArea}`}
                onClick={(e) => { e.stopPropagation(); toggleGroup(tier2GroupId); }}
              >
                <i className={`pi ${isTier2Expanded ? 'pi-chevron-down' : 'pi-chevron-right'} text-gray-600 ${styles.chevronIcon}`} />
              </div>
              <span className={`text-sm text-gray-500 font-mono ${styles.timestamp}`}>{formatTraceDateTime(firstTier2.eventTimestamp)}</span>
              <span className={`text-base flex-1 ${styles.description}`}>{firstTier2.traceGroupDescription || groupId}</span>
              <Tag value={groupId} className={`text-xs font-mono trace-group-chip ${styles.traceGroupChip}`} data-pr-tooltip={firstTier2.traceGroupDescription || groupId} data-pr-position="top" />
            </div>
          </div>
        );

        if (isTier2Expanded) {
          const nestedTimeline: Array<{ type: 'tier2' | 'tier3Group'; log: ProcessedTraceLog; tier3Logs?: ProcessedTraceLog[] }> = [];
          tier2Logs.forEach((log) => nestedTimeline.push({ type: 'tier2', log }));
          if (tier3ByParent.has(groupId)) {
            const tier3Logs = tier3ByParent.get(groupId)!;
            nestedTimeline.push({ type: 'tier3Group', log: tier3Logs[0], tier3Logs });
          }
          nestedTimeline.sort((a, b) => new Date(a.log.eventTimestamp).getTime() - new Date(b.log.eventTimestamp).getTime());

          nestedTimeline.forEach((nestedItem, nestedIndex) => {
            const isLastNested = nestedIndex === nestedTimeline.length - 1;
            if (nestedItem.type === 'tier2') {
              allElements.push(renderLog(nestedItem.log, 1, isLastNested));
            } else if (nestedItem.type === 'tier3Group' && nestedItem.tier3Logs) {
              const tier3Logs = nestedItem.tier3Logs;
              const firstTier3 = tier3Logs[0];
              const tier3GroupId = `tier3-${groupId}`;
              const isTier3Expanded = expandedGroups.has(tier3GroupId);
              const descriptionIndent = 1 * 3;

              allElements.push(
                <div key={`toggle-${tier3GroupId}`} className={`flex align-items-center py-2 ${styles.tier3ToggleRow}`}>
                  <div className="flex gap-3 align-items-center flex-1">
                    <div
                      className={`chevron-area flex align-items-center justify-content-center cursor-pointer ${styles.chevronArea}`}
                      onClick={(e) => { e.stopPropagation(); toggleGroup(tier3GroupId); }}
                    >
                      <i className={`pi ${isTier3Expanded ? 'pi-chevron-down' : 'pi-chevron-right'} text-gray-600 ${styles.chevronIcon}`} />
                    </div>
                    <span className={`text-sm text-gray-500 font-mono ${styles.timestamp}`}>{formatTraceDateTime(firstTier3.eventTimestamp)}</span>
                    <span className={`text-base flex-1 ${styles.description}`} style={{ marginLeft: `calc(${descriptionIndent}rem + 8px)` }}>
                      <div className={styles.verticalLine} style={{ left: `${-descriptionIndent / 2}rem`, top: '-0.5rem', bottom: isLastNested ? '50%' : '-0.5rem' }} />
                      <div className={styles.horizontalLine} style={{ left: `${-descriptionIndent / 2}rem`, top: '50%', width: `${descriptionIndent / 2}rem` }} />
                      {firstTier3.traceGroupDescription || firstTier3.traceGroup}
                    </span>
                    {firstTier3.traceGroup && (
                      <Tag value={firstTier3.traceGroup} className={`text-xs font-mono trace-group-chip ${styles.traceGroupChip}`} data-pr-tooltip={firstTier3.traceGroupDescription || firstTier3.traceGroup} data-pr-position="top" />
                    )}
                  </div>
                </div>
              );

              if (isTier3Expanded) {
                tier3Logs.forEach((t3Log, t3Index) => {
                  const isLastT3 = t3Index === tier3Logs.length - 1;
                  allElements.push(renderLog(t3Log, 2, isLastT3, isLastNested));
                });
              }
            }
          });
        }
      }
    });

    return allElements;
  };

  const formatJson = (str: string | null): string => {
    if (!str) return '';
    try {
      return JSON.stringify(JSON.parse(str), null, 2);
    } catch {
      return str;
    }
  };

  if (detailQuery.isLoading) {
    return (
      <AppPage title="VoX Order Detail" breadcrumbs={[{ label: 'Dashboard', to: '/dashboard' }, { label: 'VoX', to: '/products/vox' }, { label: 'Transaction Logs', to: '/vox-tracing' }, { label: 'Order Detail' }]}>
        <div className="flex justify-content-center p-5">
          <ProgressSpinner />
        </div>
      </AppPage>
    );
  }

  if (detailQuery.isError || !detailQuery.data) {
    return (
      <AppPage title="VoX Order Detail" breadcrumbs={[{ label: 'Dashboard', to: '/dashboard' }, { label: 'VoX', to: '/products/vox' }, { label: 'Transaction Logs', to: '/vox-tracing' }, { label: 'Order Detail' }]}>
        <AppCard>
          <div className="text-red-500">
            {detailQuery.isError
              ? `Error: ${detailQuery.error instanceof Error ? detailQuery.error.message : 'Failed to load'}`
              : 'VoX order not found'}
          </div>
          <Button label="Back" icon="pi pi-arrow-left" className="mt-3" onClick={() => navigate(-1)} />
        </AppCard>
      </AppPage>
    );
  }

  const detail = detailQuery.data;

  return (
    <AppPage title="VoX Order Detail" breadcrumbs={[{ label: 'Dashboard', to: '/dashboard' }, { label: 'VoX', to: '/products/vox' }, { label: 'Transaction Logs', to: '/vox-tracing' }, { label: 'Order Detail' }]}>
      <Toast ref={toast} />
      <div className="flex justify-content-between align-items-center mb-3">
        <Button
          label="Back"
          icon="pi pi-arrow-left"
          className="p-button-text"
          onClick={() => navigate(-1)}
        />
        <div className="flex gap-2">
          {detail.waterfallExecutionRecordGuid && (
            <Button
              icon="pi pi-sitemap"
              label="View Waterfall"
              className="p-button-outlined p-button-sm"
              onClick={() => navigate(`/vox-tracing/waterfall/${detail.waterfallExecutionRecordGuid}`)}
            />
          )}
          {capabilitiesQuery.data?.supportsUserInteraction && (
            <Button
              icon="pi pi-link"
              label="Generate URL"
              className="p-button-outlined p-button-sm"
              onClick={async () => {
                setGeneratingUrl(true);
                setUrlError(null);
                try {
                  const url = await createEmbedUrl(detail.voxOrderHistoryGuid);
                  setGeneratedUrl(url);
                  setUrlCopied(false);
                  setUrlModalVisible(true);
                } catch (err) {
                  setUrlError(err instanceof Error ? err.message : 'Failed to generate URL');
                } finally {
                  setGeneratingUrl(false);
                }
              }}
              loading={generatingUrl}
            />
          )}
          <Button
            icon="pi pi-refresh"
            label="Refresh"
            className="p-button-outlined p-button-sm"
            onClick={() => queryClient.invalidateQueries({ queryKey: ['vox-order-history-detail'] })}
            loading={detailQuery.isRefetching}
          />
        </div>
      </div>

      {urlError && (
        <div className="p-3 mb-3 border-round surface-ground text-red-500 text-sm">
          {urlError}
        </div>
      )}

      <AppCard>
        <div className="flex flex-column gap-4">
          <div className="flex flex-row justify-content-between align-items-center">
            <h2 className="text-xl font-bold m-0">
              {detail.integrationVendor} — {detail.transactionName}
            </h2>
            <Tag
              value={detail.status}
              severity={statusSeverity[detail.status] || 'info'}
              className="text-sm"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <span className="text-500 text-sm">Loan Number</span>
              <CopyableText value={detail.loanNumber} label="Loan Number" toast={toast} />
            </div>
            <div>
              <span className="text-500 text-sm">Loan Identifier</span>
              <CopyableText value={detail.loanIdentifier} label="Loan Identifier" toast={toast} />
            </div>
            <div>
              <span className="text-500 text-sm">Lead Number</span>
              <CopyableText value={detail.leadNumber} label="Lead Number" toast={toast} />
            </div>
            <div>
              <span className="text-500 text-sm">Vendor</span>
              <div className="font-semibold">{detail.integrationVendor}</div>
            </div>
            <div>
              <span className="text-500 text-sm">VOIE Data Status</span>
              <div className="font-semibold">
                <Tag
                  value={detail.voieProviderDataStatus}
                  severity={providerDataStatusSeverity[detail.voieProviderDataStatus] || 'info'}
                />
              </div>
            </div>
            <div>
              <span className="text-500 text-sm">VOA Data Status</span>
              <div className="font-semibold">
                <Tag
                  value={detail.voaProviderDataStatus}
                  severity={providerDataStatusSeverity[detail.voaProviderDataStatus] || 'info'}
                />
              </div>
            </div>
            <div>
              <span className="text-500 text-sm">Provider Transaction ID</span>
              <CopyableText value={detail.providerTransactionId} label="Provider Transaction ID" toast={toast} />
            </div>
            <div>
              <span className="text-500 text-sm">Provider User ID</span>
              <CopyableText value={detail.providerUserId} label="Provider User ID" toast={toast} />
            </div>
            <div>
              <span className="text-500 text-sm">VoX Order GUID</span>
              <CopyableText value={detail.voxOrderHistoryGuid} label="VoX Order GUID" toast={toast} />
            </div>
            <div>
              <span className="text-500 text-sm">Created</span>
              <div className="font-semibold">{formatDateTime(detail.createdDate)}</div>
            </div>
            <div>
              <span className="text-500 text-sm">Modified</span>
              <div className="font-semibold">{formatDateTime(detail.modifiedDate)}</div>
            </div>
          </div>
        </div>
      </AppCard>

      {(detail.requestData || detail.responseData) && (
        <AppCard className="mt-3">
          <h3 className="text-lg font-semibold mb-3">Provider Request / Response</h3>
          <div className="flex gap-2">
            {detail.requestData && (
              <Button
                label="View Request Data"
                icon="pi pi-file"
                className="p-button-outlined"
                onClick={() => {
                  setDataPanelTitle('Request Data');
                  setDataPanelContent(detail.requestData!);
                  setDataPanelVisible(true);
                }}
              />
            )}
            {detail.responseData && (
              <Button
                label="View Response Data"
                icon="pi pi-file"
                className="p-button-outlined"
                onClick={() => {
                  setDataPanelTitle('Response Data');
                  setDataPanelContent(detail.responseData!);
                  setDataPanelVisible(true);
                }}
              />
            )}
          </div>
        </AppCard>
      )}

      {detail.items && detail.items.length > 0 && (
        <AppCard className="mt-3">
          <h3 className="text-lg font-semibold mb-3">Order Items</h3>
          <PrimeDataTable value={detail.items} emptyMessage="No items">
            <Column field="externalId" header="EXTERNAL ID" />
            <Column field="itemType" header="TYPE" />
            <Column field="status" header="STATUS" body={itemStatusTemplate} />
            <Column
              field="createdDate"
              header="CREATED"
              body={(row: VoxOrderItemHistoryViewModel) => formatDateTime(row.createdDate)}
            />
          </PrimeDataTable>
        </AppCard>
      )}

      <AppCard className="mt-3">
        <div className="flex justify-content-between align-items-center mb-3">
          <h3 className="text-lg font-semibold m-0">Trace Logs</h3>
          {groupedLogs.length > 0 && (
            <div className="flex gap-2">
              <Button label="Expand All" icon="pi pi-plus" onClick={handleExpandAll} size="small" outlined />
              <Button label="Collapse All" icon="pi pi-minus" onClick={handleCollapseAll} size="small" outlined />
            </div>
          )}
        </div>
        <Tooltip target=".trace-group-chip" />
        {groupedLogs.length > 0 ? (
          <div className="trace-log-list">
            {renderFlatLogs(groupedLogs)}
          </div>
        ) : (
          <div className="text-500">No trace logs found for this order.</div>
        )}
      </AppCard>

      {detail.webhookEvents && detail.webhookEvents.length > 0 && (
        <AppCard className="mt-3">
          <h3 className="text-lg font-semibold mb-3">Webhook Events</h3>
          <PrimeDataTable
            value={detail.webhookEvents}
            selectionMode="single"
            onSelectionChange={(e: any) => {
              setSelectedWebhook(e.value as WebhookEventViewModel);
              setWebhookPanelVisible(true);
            }}
            emptyMessage="No webhook events"
            className="cursor-pointer"
            paginator
            rows={10}
          >
            <Column
              field="eventTimestamp"
              header="TIMESTAMP"
              body={(row: WebhookEventViewModel) => formatDateTime(row.eventTimestamp)}
              sortable
            />
            <Column field="eventType" header="EVENT TYPE" sortable />
            <Column
              field="httpStatus"
              header="HTTP STATUS"
              body={(row: WebhookEventViewModel) => (
                <Tag
                  value={String(row.httpStatus)}
                  severity={row.httpStatus >= 200 && row.httpStatus < 300 ? 'success' : 'danger'}
                />
              )}
              sortable
            />
            <Column field="vendor" header="VENDOR" sortable />
            <Column field="attempt" header="ATTEMPT" />
          </PrimeDataTable>
        </AppCard>
      )}

      {detail.relatedOrders && detail.relatedOrders.length > 0 && (
        <AppCard className="mt-3">
          <h3 className="text-lg font-semibold mb-3">Related Orders</h3>
          <PrimeDataTable
            value={detail.relatedOrders}
            selectionMode="single"
            onSelectionChange={(e: any) => {
              const order = e.value as VoxOrderHistorySummaryViewModel;
              if (order?.voxOrderHistoryGuid) {
                navigate(`/vox-tracing/order/${order.voxOrderHistoryGuid}`);
              }
            }}
            emptyMessage="No related orders"
            className="cursor-pointer"
          >
            <Column field="transactionName" header="TRANSACTION" sortable />
            <Column field="integrationVendor" header="VENDOR" sortable />
            <Column
              field="status"
              header="ORDER STATUS"
              body={(row: VoxOrderHistorySummaryViewModel) => (
                <Tag value={row.status} severity={statusSeverity[row.status] || 'info'} />
              )}
              sortable
            />
            <Column header="DATA STATUS" body={(row: VoxOrderHistorySummaryViewModel) => {
              const voie = row.voieProviderDataStatus;
              const voa = row.voaProviderDataStatus;
              const hasVoieData = voie === 'IncomeAndEmployment' || voie === 'EmploymentOnly';
              const hasVoaData = voa === 'Assets' || voa === 'AssetsAndIncome';
              if (hasVoieData || hasVoaData) {
                const chips: { label: string; severity: 'success' | 'info' | 'warning' | 'danger' }[] = [];
                if (voie === 'IncomeAndEmployment') chips.push({ label: 'VOIE', severity: 'success' });
                if (voie === 'EmploymentOnly') chips.push({ label: 'VOE', severity: 'info' });
                if (hasVoaData) chips.push({ label: 'VOA', severity: 'success' });
                return (
                  <div className="flex gap-1 flex-wrap">
                    {chips.map((chip, idx) => (
                      <Tag key={idx} value={chip.label} severity={chip.severity} />
                    ))}
                  </div>
                );
              }
              if (voie === 'NoData' || voa === 'NoData') {
                return <Tag value="NoData" severity="danger" />;
              }
              return <Tag value="Unknown" severity="warning" />;
            }} />
            <Column
              header="DATE STARTED"
              body={(row: VoxOrderHistorySummaryViewModel) => formatDateTime(row.createdDate)}
              sortable
            />
            <Column
              header="DATE COMPLETED"
              body={(row: VoxOrderHistorySummaryViewModel) => formatDateTime(row.modifiedDate)}
              sortable
            />
          </PrimeDataTable>
        </AppCard>
      )}

      <Sidebar
        visible={dataPanelVisible}
        position="right"
        onHide={() => setDataPanelVisible(false)}
        style={{ width: '50vw' }}
        header={
          <span className="font-bold text-lg">{dataPanelTitle}</span>
        }
      >
        <pre
          className="surface-ground border-round p-3 text-sm overflow-auto m-0"
          style={{ maxHeight: 'calc(100vh - 120px)' }}
        >
          {formatJson(dataPanelContent)}
        </pre>
      </Sidebar>

      <Sidebar
        visible={webhookPanelVisible}
        position="right"
        onHide={() => setWebhookPanelVisible(false)}
        style={{ width: '50vw' }}
        header={
          <div className="flex flex-column">
            <span className="font-bold text-lg">Webhook Event Detail</span>
            {selectedWebhook && (
              <span className="text-500 text-sm">{formatDateTime(selectedWebhook.eventTimestamp)}</span>
            )}
          </div>
        }
      >
        {selectedWebhook && (
          <div className="flex flex-column gap-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <span className="text-500 text-sm font-semibold">Event Type</span>
                <div>{selectedWebhook.eventType}</div>
              </div>
              <div>
                <span className="text-500 text-sm font-semibold">HTTP Status</span>
                <div>
                  <Tag
                    value={String(selectedWebhook.httpStatus)}
                    severity={selectedWebhook.httpStatus >= 200 && selectedWebhook.httpStatus < 300 ? 'success' : 'danger'}
                  />
                </div>
              </div>
              <div>
                <span className="text-500 text-sm font-semibold">Vendor</span>
                <div>{selectedWebhook.vendor || '—'}</div>
              </div>
              <div>
                <span className="text-500 text-sm font-semibold">Attempt</span>
                <div>{selectedWebhook.attempt}</div>
              </div>
            </div>

            <div>
              <span className="text-500 text-sm font-semibold">URL</span>
              <div className="text-sm" style={{ wordBreak: 'break-all' }}>{selectedWebhook.url}</div>
            </div>

            {selectedWebhook.requestPayloadFileId && (
              <div>
                <span className="text-500 text-sm font-semibold">Request Payload</span>
                {webhookPayloadQuery.isLoading && (
                  <div className="flex align-items-center gap-2 py-3">
                    <ProgressSpinner style={{ width: '20px', height: '20px' }} strokeWidth="4" />
                    <span className="text-sm text-500">Loading payload...</span>
                  </div>
                )}
                {webhookPayloadQuery.isError && (
                  <div className="text-red-500 text-sm p-3">Failed to load request payload</div>
                )}
                {webhookPayloadQuery.data && (
                  <pre
                    className="surface-ground border-round p-3 text-sm overflow-auto m-0"
                    style={{ maxHeight: '400px' }}
                  >
                    {formatJson(webhookPayloadQuery.data)}
                  </pre>
                )}
              </div>
            )}

            {selectedWebhook.response && (
              <div>
                <span className="text-500 text-sm font-semibold">Response</span>
                <pre
                  className="surface-ground border-round p-3 text-sm overflow-auto m-0"
                  style={{ maxHeight: '300px' }}
                >
                  {formatJson(selectedWebhook.response)}
                </pre>
              </div>
            )}
          </div>
        )}
      </Sidebar>

      <Sidebar
        visible={sidePanelVisible}
        position="right"
        onHide={() => setSidePanelVisible(false)}
        className={`trace-log-sidebar ${styles.sidebarPanel}`}
        header={
          <div className="flex align-items-center gap-2">
            <i className="pi pi-list text-xl" />
            <span className="font-semibold">Trace Log Details</span>
          </div>
        }
      >
        {selectedLog && <TraceLogDetailPanel log={selectedLog} />}
      </Sidebar>

      <Dialog
        header="Generated URL"
        visible={urlModalVisible}
        onHide={() => setUrlModalVisible(false)}
        style={{ width: '550px' }}
        modal
        footer={
          <div className="flex justify-content-end gap-2">
            <Button
              icon="pi pi-external-link"
              label="Open"
              className="p-button-outlined"
              onClick={() => {
                setUrlModalVisible(false);
                setIframeModalVisible(true);
              }}
            />
            <Button
              icon={urlCopied ? 'pi pi-check' : 'pi pi-copy'}
              label={urlCopied ? 'Copied!' : 'Copy to clipboard'}
              className={urlCopied ? 'p-button-success' : 'p-button-outlined'}
              onClick={() => {
                navigator.clipboard.writeText(generatedUrl);
                setUrlCopied(true);
                setTimeout(() => setUrlCopied(false), 2000);
              }}
            />
          </div>
        }
      >
        <div
          className="surface-ground border-round p-3 font-mono text-sm"
          style={{ wordBreak: 'break-all', userSelect: 'all' }}
        >
          {generatedUrl}
        </div>
      </Dialog>

      <Dialog
        header="Embedded View"
        visible={iframeModalVisible}
        onHide={() => setIframeModalVisible(false)}
        modal
        maximizable
        style={{ width: '768px' }}
        breakpoints={{ '768px': '100vw' }}
        contentStyle={{ padding: 0, overflow: 'hidden' }}
      >
        <iframe
          src={generatedUrl}
          title="Generated URL"
          style={{ width: '100%', height: '75vh', border: 'none' }}
        />
      </Dialog>
    </AppPage>
  );
};

export default VoxOrderDetail;
